import { useEffect, useState } from "react";
import { AdminLayout } from "./AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Power, Bell, Mail, Trash2 } from "lucide-react";

export default function AdminsManage() {
  const { admin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "admin" });
  const [recipients, setRecipients] = useState([]);
  const [rform, setRform] = useState({ email: "", name: "", role: "Leadership" });
  const isPrimary = admin?.role === "primary";

  const load = () => {
    api.get("/admin/administrators").then((r) => setAdmins(r.data)).catch(() => {});
    api.get("/admin/notification-recipients").then((r) => setRecipients(r.data)).catch(() => {});
    if (isPrimary) api.get("/admin/audit-log").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(load, [isPrimary]);

  const addRecipient = async () => {
    if (!rform.email) return toast.error("Recipient email is required");
    try { await api.post("/admin/notification-recipients", rform); toast.success("Recipient added"); setRform({ email: "", name: "", role: "Leadership" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add recipient"); }
  };
  const removeRecipient = async (r) => { try { await api.delete(`/admin/notification-recipients/${r.recipient_id}`); toast.success("Recipient removed"); load(); } catch { toast.error("Failed"); } };

  const add = async () => {
    if (!form.email) return toast.error("Email is required");
    try {
      await api.post("/admin/administrators", { email: form.email, name: form.name, role: form.role });
      toast.success("Account added");
      setForm({ email: "", name: "", role: "admin" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add administrator"); }
  };

  const toggle = async (a, field) => {
    try {
      await api.patch(`/admin/administrators/${a.admin_id}`, { [field]: !a[field] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <AdminLayout title="Administrators">
      <div className="bg-white rounded-lg border border-navy/10 shadow-sm p-6 mb-6" data-testid="notification-recipients">
        <h2 className="font-display font-bold text-navy flex items-center gap-2 mb-1"><Mail size={18} className="text-magenta" /> New-Inquiry Notification Recipients</h2>
        <p className="text-xs text-dark-text/60 mb-4">Everyone here receives an alert (with applicant name, email, interests, date and a profile link) when a new intake is submitted — in addition to the primary administrator and all active administrators.</p>
        {isPrimary && (
          <div className="grid sm:grid-cols-4 gap-3 mb-4">
            <input value={rform.email} onChange={(e) => setRform({ ...rform, email: e.target.value })} placeholder="Recipient email" data-testid="recipient-email"
              className="border border-navy/15 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-magenta" />
            <input value={rform.name} onChange={(e) => setRform({ ...rform, name: e.target.value })} placeholder="Name (optional)" data-testid="recipient-name"
              className="border border-navy/15 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-magenta" />
            <input value={rform.role} onChange={(e) => setRform({ ...rform, role: e.target.value })} placeholder="Role (e.g. Leadership)" data-testid="recipient-role"
              className="border border-navy/15 rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-magenta" />
            <button onClick={addRecipient} data-testid="add-recipient" className="bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors">Add Recipient</button>
          </div>
        )}
        {recipients.length === 0 ? <p className="text-sm text-dark-text/50">No additional recipients yet.</p> : (
          <div className="flex flex-wrap gap-2">
            {recipients.map((r) => (
              <span key={r.recipient_id} className="inline-flex items-center gap-2 bg-navy-light border border-navy/15 rounded-full pl-3 pr-2 py-1 text-xs" data-testid={`recipient-${r.email}`}>
                <span className="font-semibold text-navy">{r.name}</span><span className="text-dark-text/60">{r.email} · {r.role}</span>
                {isPrimary && <button onClick={() => removeRecipient(r)} className="text-red-600 hover:text-red-700" data-testid={`remove-recipient-${r.email}`}><Trash2 size={13} /></button>}
              </span>
            ))}
          </div>
        )}
      </div>
      {isPrimary && (
        <div className="bg-white rounded-lg border border-navy/10 shadow-sm p-6 mb-6" data-testid="add-admin-form">
          <h2 className="font-display font-bold text-navy flex items-center gap-2 mb-4"><UserPlus size={18} className="text-magenta" /> Add Administrator</h2>
          <div className="grid sm:grid-cols-4 gap-3">
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Google email address" data-testid="new-admin-email"
              className="border border-navy/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-magenta outline-none" />
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Name (optional)" data-testid="new-admin-name"
              className="border border-navy/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-magenta outline-none" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-admin-role"
              className="border border-navy/15 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-magenta outline-none bg-white">
              <option value="admin">Administrator</option><option value="trainer">Certified Trainer / Instructor</option><option value="mentor">Mentor</option>
            </select>
            <button onClick={add} data-testid="add-admin-submit"
              className="bg-magenta hover:bg-magenta-dark text-white font-bold py-2.5 px-5 rounded-md transition-colors">Add Administrator</button>
          </div>
          <p className="text-xs text-dark-text/50 mt-3">The administrator will sign in using this Google account. Each administrator has an individual, secure account.</p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-navy/10 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-navy/10"><h2 className="font-display font-bold text-navy">Administrator Accounts</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admins-table">
            <thead className="bg-navy-light text-navy">
              <tr>{["Name", "Email", "Role", "Status", "Notifications", ""].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {admins.map((a) => (
                <tr key={a.admin_id} data-testid={`admin-row-${a.email}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{a.name}</td>
                  <td className="px-4 py-3 text-dark-text/70">{a.email}</td>
                  <td className="px-4 py-3">
                    {isPrimary && a.role !== "primary" ? (
                      <select value={a.role} data-testid={`role-select-${a.email}`} onChange={async (e) => { try { await api.patch(`/admin/administrators/${a.admin_id}`, { role: e.target.value }); toast.success("Role updated"); load(); } catch { toast.error("Update failed"); } }}
                        className="text-xs font-bold px-2 py-1 rounded-md border border-navy/15 bg-navy-light text-navy">
                        <option value="admin">Admin</option><option value="trainer">Trainer</option><option value="mentor">Mentor</option>
                      </select>
                    ) : (
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${a.role === "primary" ? "bg-magenta-light text-magenta" : "bg-navy-light text-navy"}`}>
                      {{ primary: "Primary", admin: "Admin", trainer: "Trainer", mentor: "Mentor" }[a.role] || "Admin"}
                    </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold ${a.active ? "text-magenta" : "text-red-600"}`}>{a.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-4 py-3">
                    {(isPrimary || a.email === admin?.email) ? (
                      <button onClick={() => toggle(a, "notifications_enabled")} data-testid={`toggle-notif-${a.email}`}
                        className={`inline-flex items-center gap-1 text-xs font-semibold ${a.notifications_enabled ? "text-navy" : "text-dark-text/40"}`}>
                        <Bell size={14} /> {a.notifications_enabled ? "On" : "Off"}
                      </button>
                    ) : (
                      <span className="text-xs text-dark-text/40">{a.notifications_enabled ? "On" : "Off"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isPrimary && a.role !== "primary" && (
                      <button onClick={() => toggle(a, "active")} data-testid={`toggle-active-${a.email}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:text-magenta">
                        <Power size={14} /> {a.active ? "Deactivate" : "Activate"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isPrimary && audit.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-navy/10 shadow-sm p-5">
          <h2 className="font-display font-bold text-navy flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-magenta" /> Audit Record</h2>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {audit.map((e) => (
              <div key={e.id} className="text-xs text-dark-text/70 flex justify-between border-b border-navy/5 pb-2">
                <span><strong className="text-navy">{e.action}</strong> — {e.target} by {e.actor}</span>
                <span className="text-dark-text/40">{new Date(e.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
